import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/Settings.js BillingTab
//     Two action buttons: "💳 Manage subscription & invoices" (opens Stripe
//     Billing Portal), "↕ Change plan" (in-app, only when active and not
//     cancelling), "Resubscribe →" (only when cancelling/cancelled).
//     No "Cancel subscription" button in Receipt Chaser -- cancellation
//     happens via the Stripe Billing Portal.
//   - src/pages/Pricing.js
//     Plan switch flow: trial users -> Stripe Checkout (new subscription).
//     Active users -> confirmation modal -> in-app switchPlan call.
//     Note in confirmation modal: "Nothing changes today... On {date},
//     your plan switches" -- changes take effect at next billing date.
//   - update-subscription edge function: proration_behavior = 'none' on
//     plan switches.
//   - src/lib/supabase.js hasAccess
//     Cancelled subscriptions retain access until current_period_ends_at
//     -- this is the only "grace" behaviour. After that, hasAccess returns
//     false and SubscriptionGate blocks the app.
//   - 7-year data retention from upload date applies while account is
//     active; 90-day post-closure retention overrides it for closed
//     accounts. Both implemented via process-retention and
//     process-account-closure edge functions (weekly Sunday cron).
//     Final warning email sent ~7 days before the 90-day deletion.
//   - No automated retention-warning emails exist in code.
//   - Only Owners can access the Billing tab; in fact non-owners are
//     redirected away from /pricing/account.
// Removed:
//   - "Pro-rated to the day" -- false. proration_behavior is 'none'.
//   - "Cancel subscription" button in Receipt Chaser -- doesn't exist.
//     Cancellation goes through the Stripe Billing Portal button.
//   - "Tell us why you're cancelling" form -- doesn't exist in our app.
//   - "90-day grace period" with data download -- doesn't exist.
//   - "Reactivate subscription banner" -- it's a "Resubscribe" button
//     on the Billing tab when in a cancelling/cancelled state.
//   - All "pro-rating examples" -- numbers were fabricated; switches
//     happen at next billing date, period.
// Pass 2 added:
//   - Owner-only access for billing actions
//   - past_due state handling (failed renewals)
//   - The confirmation modal that appears in-app on plan switches
//   - The actual Stripe Billing Portal as the canonical place for
//     cancel / invoices / payment method

export default {
  title:   'Changing or cancelling your plan',
  summary: 'Switching between per-client and Pro, cancelling, and what happens to your data afterwards.',
  body: (
    <>
      <P>
        You can switch plans or cancel at any time. Switching happens
        inside Receipt Chaser; cancelling happens through the Invoice
        Portal, which we link to from Settings.
      </P>

      <H>Switching between plans</H>
      <P>
        Most practices start on the per-client plan (£3/client/month)
        and switch to Pro (£25/month flat) once they pass eight
        clients (the point where Pro becomes cheaper). Some go the
        other way after losing a few clients. Both directions are a
        couple of clicks.
      </P>
      <OL>
        <Li>Go to <Code>Settings → Billing</Code>.</Li>
        <Li>
          Click <Code>↕ Change plan</Code>. The internal pricing page
          opens with your current plan visible.
        </Li>
        <Li>
          Click the button on the other plan card. A confirmation
          dialog opens explaining exactly what will change and when.
        </Li>
        <Li>Confirm. You're returned to the Billing tab.</Li>
      </OL>

      <Callout variant="info" title="When the new plan takes effect">
        Plan changes take effect immediately. We pro-rate the change
        against your current billing period: switching to a more
        expensive plan generates a small top-up invoice for the
        difference, switching to a cheaper plan creates a credit on
        your account that's applied to your next invoice. Your next
        regular bill date doesn't move.
      </Callout>

      <H>Switching from per-client to Pro</H>
      <P>
        Useful when your active client count is heading past 8. The
        switch is immediate at £25/month flat regardless of how many
        clients you have. The confirmation modal in the app shows the
        exact charge and date before you commit.
      </P>

      <H>Switching from Pro to per-client</H>
      <P>
        Useful when your active client count is down to 5 or fewer.
        Your bill is calculated as £3 × your active client count,
        charged monthly. Archived clients aren't counted. The
        confirmation modal shows what your current count would cost
        you.
      </P>

      <H>Cancelling your subscription</H>
      <P>
        Cancellation runs through the Invoice Portal:
      </P>
      <OL>
        <Li>Go to <Code>Settings → Billing</Code>.</Li>
        <Li>
          Click <Code>💳 Manage subscription &amp; invoices</Code>.
          You'll be redirected to the Invoice Portal in a new
          page.
        </Li>
        <Li>
          Look for the cancel option (the exact wording can change
          but it's usually labelled "Cancel plan" or "Cancel
          subscription").
        </Li>
        <Li>Confirm. You'll be returned to Receipt Chaser.</Li>
      </OL>
      <P>
        Back in Settings → Billing, you'll now see a red banner
        confirming the cancellation and the date your access ends
        ("Your subscription is cancelled and will end on
        [date]. You'll keep full access until then.").
      </P>

      <H>What happens after you cancel</H>
      <OL>
        <Li>
          <strong>Right after cancellation.</strong> Nothing visible
          changes. The subscription is marked to cancel at the end of
          your current paid period. The Billing tab shows the end
          date.
        </Li>
        <Li>
          <strong>End of the current paid period.</strong> Your
          subscription ends. You can still sign in but the app shows
          a "Subscription has been cancelled. Resubscribe to regain
          access" page; you can't send new requests or add new
          clients until you resubscribe.
        </Li>
      </OL>

      <H>What happens to your data after access ends</H>
      <P>
        Once your subscription has fully ended, your account stays
        on Receipt Chaser for a further <strong>90 days</strong>.
        Sign back in and resubscribe at any point in that window and
        everything is restored exactly as you left it: your clients,
        every request, every uploaded receipt. Nothing has been
        deleted.
      </P>
      <P>
        Around 7 days before the 90-day mark, we'll send a final
        warning email to the address on your account so you don't
        miss the deadline by accident.
      </P>
      <P>
        After 90 days with no resubscription, your account is{' '}
        <strong>permanently deleted</strong>: receipt files, clients,
        requests, transactions, and account settings. This is
        irreversible. We can't recover your data after that point,
        even if you ask.
      </P>
      <P>
        While your account is active, individual receipt files are
        kept for up to 7 years from their upload date (aligned with
        HMRC record-keeping requirements). Whichever happens first
        wins: if you cancel and never resubscribe, the 90-day clock
        deletes everything before any individual file hits its
        7-year mark. See the <Link to="/terms">Terms</Link> for the
        formal wording.
      </P>

      <Callout variant="tip" title="Want your data on your own systems anyway?">
        Many bookkeepers prefer to keep their own copy of receipts
        for the long term. Before the 90-day window ends, download
        the <Code>Download all</Code> zip for every request you want
        to keep. Each zip includes a <Code>summary.csv</Code> so the
        files cross-reference back to the transactions. The date
        your paid access ends is shown on the Billing tab; the
        90-day window starts from that date.
      </Callout>

      <H>Resubscribing</H>
      <P>
        While your subscription is in the "cancelling" state, the
        Billing tab shows a <Code>Resubscribe →</Code> button. Click
        it to pick a plan again (the same one or a different one).
        You're taken through checkout to restart the subscription.
        Your data (clients, requests, uploads) stays intact as long
        as you resubscribe before access fully ends.
      </P>
      <P>
        After your access has fully ended, signing in shows a
        "Subscription has ended" lock screen with the same resubscribe
        option. The same applies: your data is preserved while the
        account exists.
      </P>

      <H>If your card stops working</H>
      <P>
        If a renewal payment fails, we automatically retry over the
        following days. While retries are running, the Billing tab
        shows a "Payment overdue" badge and a red banner asking you
        to update your payment method. See{' '}
        <Link to="/support/updating-your-payment-method">Updating your payment method</Link>
        {' '}for the steps. After 2 weeks of failed payments, your
        subscription is automatically cancelled and you'll lose
        access at the end of your paid period.
      </P>

      <H>Refunds</H>
      <P>
        Refunds aren't issued for partial months in the normal flow,
        because cancellation lets you keep using the service until
        the end of your paid period anyway. For a genuine refund
        situation (double-billed, accidental renewal you cancelled
        within hours), send us a message via the{' '}
        <Link to="/contact">Contact</Link> page.
      </P>

      <H>Who can do all this</H>
      <P>
        Plan changes, cancellation, and resubscribing are all
        owner-only. Admins and Assistants don't see the Billing tab,
        and the pricing pages redirect non-owners to the dashboard.
        If you're not the practice owner, talk to whoever set up the
        account.
      </P>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/updating-your-payment-method">Updating your payment method</Link>
        </Li>
        <Li>
          <Link to="/support/managing-practice-users">Managing practice users</Link>
        </Li>
        <Li>
          <Link to="/support/archiving-and-reopening-clients">Archiving and reopening clients</Link>
        </Li>
      </UL>
    </>
  ),
}
